import type { ProjectLabels } from '@shared/models/project-label.model';
import { useEffect, useMemo } from 'react';
import { useSelectedOrganizationName } from '@client/store/organization';
import { useSearchParam } from '@client/hooks/use-search-param.hook';
import { useLabelsStore } from './project-labels.store';

export {
  loadProjectLabels,
  createProjectLabel,
  assignProjectLabels,
} from './project-labels.store';

const EMPTY: ProjectLabels & { loaded: boolean } = {
  labels: [],
  assignments: {},
  loaded: false,
};

export const useProjectLabels = () => {
  const organization = useSelectedOrganizationName() ?? '';
  const data = useLabelsStore(
    (state) => state.organizations[organization] ?? EMPTY,
  );
  const labels = useMemo(() => {
    const assignedIds = new Set(Object.values(data.assignments).flat());
    return data.labels.filter((label) => assignedIds.has(label.id));
  }, [data]);
  return { ...data, labels, organization };
};

/** Comma-separated UUIDs keep the filter shareable and independent of project search. */
export const useProjectLabelFilter = () => {
  const [filter, setFilter] = useSearchParam('p-labels', '');
  const { labels, loaded } = useProjectLabels();
  useEffect(() => {
    if (!loaded) return;
    const availableIds = new Set(labels.map((label) => label.id));
    const next = filter
      .split(',')
      .filter((id) => availableIds.has(id))
      .join(',');
    if (next !== filter) setFilter(next);
  }, [filter, labels, loaded, setFilter]);
  return [filter, setFilter] as const;
};
